import { useState, useEffect } from 'react';
import { apiCall, ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { FaPlus, FaEye, FaPencilAlt, FaTrash, FaSpinner, FaAddressBook } from 'react-icons/fa';
import ContactModal from '../../components/ContactModal';
import { Contact, ContactType, ContactFormData } from '../../types/contact';

const AddressBook = () => {
	const { token } = useAuth();
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [activeMainTab, setActiveMainTab] = useState<string>('All');
	const [activeSubTab, setActiveSubTab] = useState<string>('Active');
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [editingContact, setEditingContact] = useState<Contact | null>(null);
	const [contacts, setContacts] = useState<Contact[]>([]);
	const [allContacts, setAllContacts] = useState<Contact[]>([]); // Store all contacts for counting
	const [contactTypes, setContactTypes] = useState<ContactType[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);        const mainTabs = [
                'All',
                'Referrers',
                'Providers', 
                'Clinics',
                'Hospitals',
                'Insurers',
                'Health Funds',
                'Law Firms',
                'Businesses',
                'Suppliers'
        ];

        // Load data on component mount
        useEffect(() => {
                loadAllContacts(); // Load all contacts for counting
                loadContacts();
                loadContactTypes();
        }, []);

        // Reload contacts when filters change
        useEffect(() => {
                loadContacts();
        }, [activeMainTab, activeSubTab, searchTerm]);

        // Load all contacts for accurate counting (no filters except main tab)
        const loadAllContacts = async () => {
                try {
                        let url = ENDPOINTS.CORE.CONTACTS;
                        const params = new URLSearchParams();
                        
                        // Only filter by main tab for counting purposes
                        if (activeMainTab !== 'All') {
                                if (activeMainTab === 'Referrers') {
                                        params.append('is_referrer', 'true');
                                } else {
                                        const typeMap: Record<string, string> = {
                                                'Providers': 'provider',
                                                'Clinics': 'clinic',
                                                'Hospitals': 'hospital',
                                                'Insurers': 'insurer',
                                                'Health Funds': 'health_fund',
                                                'Law Firms': 'law_firm',
                                                'Businesses': 'business',
                                                'Suppliers': 'supplier'
                                        };
                                        params.append('contact_type', typeMap[activeMainTab] || activeMainTab.toLowerCase());
                                }
                        }
                        
                        if (params.toString()) {
                                url += `?${params.toString()}`;
                        }

                        const response = await apiCall(url, {
                                method: 'GET'
                        }, token || undefined);
                        
                        if (response.ok) {
                                const data = await response.json();
                                setAllContacts(data.results || data);
                        }
                } catch (error) {
                        console.error('Error loading all contacts for counts:', error);
                }
        };

        const loadContacts = async () => {
                setIsLoading(true);
                try {
                        let url = ENDPOINTS.CORE.CONTACTS;
                        const params = new URLSearchParams();
                        
                        if (activeMainTab !== 'All') {
                                if (activeMainTab === 'Referrers') {
                                        params.append('is_referrer', 'true');
                                } else {
                                        // Map display name to contact type code
                                        const typeMap: Record<string, string> = {
                                                'Providers': 'provider',
                                                'Clinics': 'clinic',
                                                'Hospitals': 'hospital',
                                                'Insurers': 'insurer',
                                                'Health Funds': 'health_fund',
                                                'Law Firms': 'law_firm',
                                                'Businesses': 'business',
                                                'Suppliers': 'supplier'
                                        };
                                        params.append('contact_type', typeMap[activeMainTab] || activeMainTab.toLowerCase());
                                }
                        }
                        
                        if (activeSubTab !== 'All') {
                                params.append('is_active', activeSubTab === 'Active' ? 'true' : 'false');
                        }
                        
                        if (searchTerm) {
                                params.append('search', searchTerm);
                        }
                        
                        if (params.toString()) {
                                url += `?${params.toString()}`;
                        }

                        const response = await apiCall(url, {
                                method: 'GET'
                        }, token || undefined);
                        if (response.ok) {
                                const data = await response.json();
                                setContacts(data.results || data);
                        } else {
                                console.error('Response status:', response.status, response.statusText);
                                throw new Error(`Failed to load contacts: ${response.status} ${response.statusText}`);
                        }
                } catch (error) {
                        console.error('Error loading contacts:', error);
                        setError('Failed to load contacts');
                } finally {
                        setIsLoading(false);
                }
        };

        // Reload all contacts when main tab changes
        useEffect(() => {
                loadAllContacts();
        }, [activeMainTab]);

        const loadContactTypes = async () => {
                if (!token) return;
                try {
                        const response = await apiCall(`${ENDPOINTS.CORE.CONTACT_TYPES}?is_active=true&page_size=200`, {
                                method: 'GET'
                        }, token);
                        if (response.ok) {
                                const data = await response.json();
                                setContactTypes(data.results || data);
                        } else {
                                console.error('Failed to load contact types:', response.status, response.statusText);
                        }
                } catch (error) {
                        console.error('Error loading contact types:', error);
                }
        };

        const handleAddContact = () => {
                setEditingContact(null);
                setIsModalOpen(true);
        };

        const handleEditContact = async (contact: Contact) => {
                // Fetch the full contact details before editing
                try {
                        const response = await apiCall(`${ENDPOINTS.CORE.CONTACTS}${contact.id}/`, {
                                method: 'GET'
                        }, token || undefined);
                        
                        if (response.ok) {
                                const fullContact = await response.json();
                                setEditingContact(fullContact);
                                setIsModalOpen(true);
                        } else {
                                console.error('Failed to load contact details:', response.status, response.statusText);
                                alert('Failed to load contact details');
                        }
                } catch (error) {
                        console.error('Error loading contact details:', error);
                        alert('Failed to load contact details');
                }
        };

        const handleSaveContact = async (contactData: ContactFormData) => {
                try {
                        const url = editingContact 
                                ? `${ENDPOINTS.CORE.CONTACTS}${editingContact.id}/`
                                : ENDPOINTS.CORE.CONTACTS;
                        
                        const method = editingContact ? 'PUT' : 'POST';
                        
                        const response = await apiCall(url, {
                                method,
                                headers: {
                                        'Content-Type': 'application/json',
                                },
                                body: JSON.stringify(contactData),
                        }, token || undefined);

                        if (response.ok) {
                                await loadContacts(); // Reload the contacts list
                                await loadAllContacts(); // Reload counts
                                setIsModalOpen(false);
                                setEditingContact(null);
                        } else {
                                const errorData = await response.json();
                                throw new Error(errorData.message || 'Failed to save contact');
                        }
                } catch (error) {
                        console.error('Error saving contact:', error);
                        throw error; // Re-throw to let the modal handle the error
                }
        };

        const handleDeleteContact = async (contactId: number) => {
                if (!confirm('Are you sure you want to delete this contact?')) {
                        return;
                }

                try {
                        const response = await apiCall(`${ENDPOINTS.CORE.CONTACTS}${contactId}/`, {
                                method: 'DELETE'
                        }, token || undefined);

                        if (response.ok) {
                                await loadContacts();
                                await loadAllContacts(); // Reload counts
                        } else {
                                console.error('Failed to delete contact:', response.status, response.statusText);
                                throw new Error(`Failed to delete contact: ${response.status} ${response.statusText}`);
                        }
                } catch (error) {
                        console.error('Error deleting contact:', error);
                        alert('Failed to delete contact');
                }
        };

        return (
                <div className="p-6">
                        {/* Page Header with Create Button */}
                        <div className="mb-6 flex justify-between items-center">
                                <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
                                        <FaAddressBook className="text-primary" />
                                        Address Book • {activeMainTab.toLowerCase()} • {activeSubTab.toLowerCase()}
                                </h1>
                                <button 
                                        className="btn btn-primary"
                                        onClick={handleAddContact}
                                        disabled={isLoading}
                                >
                                        <FaPlus className="mr-2" />
                                        Add Contact
                                </button>
                        </div>

                        <div className="bg-base-100 rounded-lg shadow p-6">
                                {/* Search Section */}
                                <div className="mb-6">
                                        <div className="relative max-w-xl">
                                                <input
                                                        type="text"
                                                        placeholder="Search contacts..."
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                        className="input input-bordered w-full"
                                                />
                                        </div>
                                </div>

                                {/* Main Tabs */}
                                <div className="tabs tabs-bordered mb-4">
                                        {mainTabs.map((tab) => (
                                                <button
                                                        key={tab}
                                                        className={`tab tab-lg ${activeMainTab === tab ? 'tab-active' : ''}`}
                                                        onClick={() => setActiveMainTab(tab)}
                                                >
                                                        {tab}
                                                </button>
                                        ))}
                                </div>

                                {/* Sub Tabs */}
                                <div className="tabs tabs-bordered mb-6">
                                        <button
                                                className={`tab tab-lg ${activeSubTab === 'Active' ? 'tab-active' : ''}`}
                                                onClick={() => setActiveSubTab('Active')}
                                        >
                                                Active
                                                {!isLoading && (
                                                        <span className="ml-2 badge badge-sm">
                                                                {allContacts.filter(c => c.is_active).length}
                                                        </span>
                                                )}
                                        </button>
                                        <button
                                                className={`tab tab-lg ${activeSubTab === 'Inactive' ? 'tab-active' : ''}`}
                                                onClick={() => setActiveSubTab('Inactive')}
                                        >
                                                Inactive
                                                {!isLoading && (
                                                        <span className="ml-2 badge badge-sm">
                                                                {allContacts.filter(c => !c.is_active).length}
                                                        </span>
                                                )}
                                        </button>
                                </div>

                                {error && (
                                        <div className="alert alert-error mb-6">
                                                <span>{error}</span>
                                        </div>
                                )}

                                {isLoading ? (
                                        <div className="flex justify-center items-center h-64">
                                                <FaSpinner className="animate-spin text-4xl" />
                                                <span className="ml-4 text-lg">Loading contacts...</span>
                                        </div>
                                ) : (
                                        <div className="overflow-x-auto">
                                                <table className="table table-zebra bg-base-100/50">
                                                        <thead>
                                                                <tr>
                                                                        <th>Name</th>
                                                                        <th>Company</th>
                                                                        <th>Type</th>
                                                                        <th>Specialty</th>
                                                                        <th>Contact</th>
                                                                        <th>Location</th>
                                                                        <th>Status</th>
                                                                        <th>Actions</th>
                                                                </tr>
                                                        </thead>
                                                        <tbody>
                                                                {contacts.length === 0 ? (
                                                                        <tr>
                                                                                <td colSpan={8} className="text-center py-8">
                                                                                        No contacts found
                                                                                </td>
                                                                        </tr>
                                                                ) : (
                                                                        contacts.map((contact) => (
                                                                                <tr key={contact.id}>
                                                                                        <td>
                                                                                                <div>
                                                                                                        <div className="font-semibold">{contact.full_name}</div>
                                                                                                        {contact.qualifications && (
                                                                                                                <div className="text-sm opacity-60">{contact.qualifications}</div>
                                                                                                        )}
                                                                                                </div>
                                                                                        </td>
                                                                                        <td>{contact.company || '-'}</td>
                                                                                        <td>
                                                                                                <span className="badge badge-outline">
                                                                                                        {contact.contact_type_name || 'Unknown'}
                                                                                                </span>
                                                                                        </td>
                                                                                        <td>{contact.specialty || '-'}</td>
                                                                                        <td>
                                                                                                <div>
                                                                                                        {contact.phone && <div>{contact.phone}</div>}
                                                                                                        {contact.email && <div className="text-sm opacity-60">{contact.email}</div>}
                                                                                                </div>
                                                                                        </td>
                                                                                        <td>
                                                                                                {contact.full_address ? (
                                                                                                        <div className="text-sm">
                                                                                                                {contact.suburb && contact.state ? `${contact.suburb}, ${contact.state}` : contact.full_address}
                                                                                                        </div>
                                                                                                ) : '-'}
                                                                                        </td>
                                                                                        <td>
                                                                                                <span className={`badge ${contact.is_active ? 'badge-success' : 'badge-error'}`}>
                                                                                                        {contact.is_active ? 'Active' : 'Inactive'}
                                                                                                </span>
                                                                                        </td>
                                                                                        <td>
                                                                                                <div className="flex gap-2">
                                                                                                        {contact.is_referrer && (
                                                                                                                <button
                                                                                                                        className="btn btn-ghost btn-xs"
                                                                                                                        title="View Referral Details"
                                                                                                                        onClick={() => {/* Navigate to referrer details */}}
                                                                                                                >
                                                                                                                        <FaEye className="w-4 h-4" />
                                                                                                                </button>
                                                                                                        )}
                                                                                                        <button 
                                                                                                                className="btn btn-ghost btn-xs"
                                                                                                                onClick={() => handleEditContact(contact)}
                                                                                                        >
                                                                                                                <FaPencilAlt className="w-4 h-4" />
                                                                                                        </button>
                                                                                                        <button
                                                                                                                className="btn btn-ghost btn-xs text-error"
                                                                                                                onClick={() => contact.id && handleDeleteContact(contact.id)}
                                                                                                        >
                                                                                                                <FaTrash className="w-4 h-4" />
                                                                                                        </button>
                                                                                                </div>
                                                                                        </td>
                                                                                </tr>
                                                                        ))
                                                                )}
                                                        </tbody>
                                                </table>
                                        </div>
                                )}

                                {/* Pagination */}
                                {contacts.length > 0 && (
                                        <div className="flex items-center justify-between mt-4">
                                                <div className="text-sm opacity-60">
                                                        Showing {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
                                                </div>
                                                <div className="join">
                                                        <button className="join-item btn btn-sm" disabled>Previous</button>
                                                        <button className="join-item btn btn-sm btn-active">1</button>
                                                        <button className="join-item btn btn-sm" disabled>Next</button>
                                                </div>
                                        </div>
                                )}
                        </div>

                        {/* Contact Modal */}
                        <ContactModal
                                isOpen={isModalOpen}
                                onClose={() => setIsModalOpen(false)}
                                onSave={handleSaveContact}
                                editContact={editingContact}
                                contactTypes={contactTypes}
                        />
                </div>
        );
};

export default AddressBook;